// Libs
import React, { useEffect, useState } from 'react';
import { Tooltip, Modal } from 'antd';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import * as Yup from 'yup';

// Own Libs
import { Button, FormContext, FormDatePicker, FormInput, FormSubmitButton, MasterDetailForm, Row } from '@digital/ui';

//Api
import { ISasPropostaOrdemCompra, ISasPropostaOrdemCompraDocs, registroSolicitacaoChancelaGJMinuta } from '@app/api/sas/sasProposta';

//Files
import { editandoState, modalAcoesIdState, vendorListDocsState } from '../atoms';
import { UploadFile, Visibility } from '@app/components';

//Utils
import { isObjNotEmpty, vtObjToFormData } from '@app/helpers/objHelper';
import ModalConfirmContentJustificativa from '@app/components/ModalConfirmContent/ModalConfirmContentJustificativa';
import { IArquivo } from '@app/types/core.types';

interface Props {
    sasProposta: ISasPropostaOrdemCompra;
}

export const ModalRegistroSolicitacaoChancelaGJMinuta = ({ sasProposta }: Props) => {
    const setModalAcoesId = useSetRecoilState(modalAcoesIdState);
    const editando = useRecoilValue(editandoState);

    const vendorListDocs = useRecoilValue(vendorListDocsState);
    const [doc, setDoc] = useState<ISasPropostaOrdemCompraDocs>({} as ISasPropostaOrdemCompraDocs);

    const [exibirModal, setExibirModal] = useState(false);

    useEffect(() => {
        var doc = vendorListDocs.find(f => f.id == sasProposta.id);
        if (isObjNotEmpty(doc)) {
            setDoc(doc || {} as ISasPropostaOrdemCompraDocs);
        }
    }, [exibirModal]);

    const handleCancel = () => {
        setExibirModal(false);
    };

    const handleCancelAndClose = () => {
        setExibirModal(false);
        setModalAcoesId('');
    };

    const handleSubmit = (values: any) => {
        const form = vtObjToFormData(values);

        if (editando.sasPropostaId == sasProposta.id) {
            Modal.confirm({
                title: <>{editando.justificativa}</>,
                content: <ModalConfirmContentJustificativa
                    texto='É necessário informar a justificativa para a alteração.'
                    setJustificativa={e => {
                        form.set('justificativaEdicao', e);
                    }}
                />,
                okText: 'Confirmar',
                cancelText: 'Cancelar',
                onOk: async (e) => {
                    form.append('editando', 'true');
                    return new Promise((resolve, reject) => {
                        registroSolicitacaoChancelaGJMinuta(sasProposta.id, form)
                            .then((data: any) => {
                                if (data && data.valid) {
                                    return resolve(true);
                                } else {
                                    if (data.notifications && data.notifications.length > 0) {
                                        const erroJustificativaEdicao = data.notifications.findIndex((c: any) => {
                                            return c.property == 'justificativaEdicao';
                                        });
                                        if (erroJustificativaEdicao >= 0) {
                                            return reject(false);
                                        }
                                    }
                                }
                            })
                            .finally(handleCancelAndClose);
                    });
                },
                onCancel: () => { setModalAcoesId(''); }
            });
        } else {
            registroSolicitacaoChancelaGJMinuta(sasProposta.id, form)
                .then((data: any) => {
                    if (data && data.valid) {
                        handleCancelAndClose();
                    }
                });
        }
    };

    return (
        <>
            <Tooltip title="Registrar Chancela">
                <Button icon='business-set_as_resume' onClick={() => { setExibirModal(true); }} />
            </Tooltip>
            <Visibility visible={exibirModal}>

                <Modal title={`Registrar Chancela`} width='780px' visible={exibirModal} onCancel={handleCancel} footer={null} >
                    <Visibility visible={isObjNotEmpty(sasProposta) && exibirModal} >
                        <MasterDetailForm
                            key={`formEnvioConfide_${sasProposta.id}`}
                            actions={[
                                <Button title='fechar' type='link' onClick={() => handleCancel()} />,
                                <FormSubmitButton title='Salvar' type='primary' noIcon onClick={handleSubmit} />
                            ]}
                            initialValues={sasProposta}
                            validationSchema={
                                Yup.object().shape({
                                    arquivo: isObjNotEmpty(doc) && doc.anexoRegistroSolicitacaoChancelaGJMinuta ? Yup.mixed().notRequired() : Yup.mixed().required('É necessário anexar um arquivo.'),
                                })
                            }
                        >
                            <FormContext.Consumer>
                                {({ formProps }) => (
                                    <>
                                        <Row>
                                            <FormInput
                                                name="proponente"
                                                label='Proponente'
                                                value={sasProposta.sasFornecedorOcNome}
                                                disabled
                                                span={6} />

                                            <FormDatePicker
                                                format='DD/MM/YYYY'
                                                name='dataRegistroChancelaSolicitacaoChancelaGJMinuta'
                                                label='Data da Chancela'
                                                required
                                                span={6} />

                                        </Row>
                                        <br></br>
                                        <Row>
                                            <UploadFile
                                                idDoComponente='arquivo-ModalStatusCadastroResultadoAvaliacaoFinanceira'
                                                type='primary'
                                                hasFile={false}
                                                hasLabel={true}
                                                icon='ui-upload'
                                                span={6}
                                                onGetArquivo={() => {

                                                    if (isObjNotEmpty(doc.anexoRegistroSolicitacaoChancelaGJMinuta)) {
                                                        return Promise.resolve({
                                                            id: doc.anexoRegistroSolicitacaoChancelaGJMinuta.id,
                                                            nome: doc.anexoRegistroSolicitacaoChancelaGJMinuta.nome,
                                                            status: "",
                                                            statusArquivoId: doc.anexoRegistroSolicitacaoChancelaGJMinuta.statusArquivoId || "",
                                                        } as IArquivo);
                                                    }
                                                    return Promise.resolve(undefined);

                                                }}
                                                showDownload={true}
                                            />
                                        </Row>
                                    </>
                                )}
                            </FormContext.Consumer>

                        </MasterDetailForm>
                    </Visibility>
                </Modal>

            </Visibility>
        </>
    );
};